import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, MailCheck } from "lucide-react";
import ReCAPTCHA from "react-google-recaptcha";
import Seo from "@/components/Seo";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

export default function ForgotPassword() {
  const { t } = useTranslation();
  const captchaRef = useRef(null);
  const [email, setEmail] = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!captchaToken) { setError(t("auth.robotCheck")); return; }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, recaptcha_token: captchaToken });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setCaptchaToken(null);
      captchaRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <Seo title="Forgot Password" description="Reset your Fire Alarm Blueprint Trainer password." path="/forgot-password" noindex />
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0A192F] blueprint-grid relative">
        <Link to="/" className="relative z-10">
          <img src="/logo-firealarmbt-light.png" alt="FireAlarm BT — Fire Alarm Blueprint Trainer" className="h-9 w-auto" />
        </Link>
        <div className="relative z-10 max-w-md">
          <p className="font-heading text-3xl font-black leading-tight mb-4">{t("auth.forgotTitle", "Forgot your password?")}</p>
          <p className="text-slate-400">{t("auth.forgotSubtitle", "Enter your email and we'll send you a link to reset it.")}</p>
        </div>
        <p className="text-xs text-slate-600 font-mono relative z-10">{t("footer.disclaimerTitle")}</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        {sent ? (
          <div className="w-full max-w-sm text-center" data-testid="forgot-password-sent">
            <span className="grid place-items-center w-16 h-16 rounded-full bg-green-500/15 text-green-400 mx-auto mb-5"><MailCheck size={32} /></span>
            <h1 className="font-heading text-3xl font-black mb-1">{t("auth.forgotSentTitle", "Check your email")}</h1>
            <p className="text-slate-400 text-sm mb-4">{t("auth.forgotSent", "If an account exists for that email, we've sent a password reset link. It expires shortly.")}</p>
            <p className="text-xs text-slate-500 mb-8">{t("auth.forgotSentHint", "The link expires in about 2 hours, and requesting a new one replaces any previous link — so use the most recent email.")}</p>
            <Button asChild data-testid="forgot-password-back" className="w-full bg-primary hover:bg-primary/90 text-white h-11">
              <Link to="/login">{t("auth.signInLink")}</Link>
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="w-full max-w-sm" data-testid="forgot-password-form">
            <h1 className="font-heading text-3xl font-black mb-1">{t("auth.forgotTitle", "Forgot your password?")}</h1>
            <p className="text-slate-400 mb-8 text-sm">{t("auth.forgotSubtitle", "Enter your email and we'll send you a link to reset it.")}</p>

            {error && <div data-testid="forgot-password-error" className="mb-4 text-sm rounded-md border border-primary/40 bg-primary/10 text-red-200 px-3 py-2">{error}</div>}

            <div>
              <Label htmlFor="email" className="text-slate-300">{t("auth.email")}</Label>
              <Input id="email" data-testid="forgot-password-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 bg-secondary border-white/15 focus-visible:ring-primary" placeholder="you@company.com" />
            </div>

            <div className="mt-6" data-testid="forgot-password-recaptcha">
              <ReCAPTCHA
                ref={captchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                theme="dark"
                onChange={setCaptchaToken}
                onExpired={() => setCaptchaToken(null)}
                onErrored={() => setCaptchaToken(null)}
              />
            </div>

            <Button type="submit" data-testid="forgot-password-submit" disabled={loading}
              className="w-full mt-6 bg-primary hover:bg-primary/90 text-white h-11">
              {loading ? <Loader2 className="animate-spin" size={18} /> : t("auth.sendResetLink", "Send reset link")}
            </Button>

            <p className="text-sm text-slate-400 mt-6 text-center">
              <Link to="/login" className="text-primary hover:underline font-medium">{t("auth.signInLink")}</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
